import { useTranslation } from 'react-i18next';
import { Plus, Trash2, ArrowRight } from 'lucide-react';
import { Button, Input, Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import { parseIpRangeInput, isParseError } from '@/lib/ip/parse-ip-range';

interface IpRangeInputProps {
  /** Roh-Eingaben (eine pro Zeile). Leeres Array → eine leere Zeile. */
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

/**
 * Multi-Range-Eingabe (Issue #318). Jede Zeile akzeptiert CIDR oder Start-Ende-
 * Range und zeigt live die resultierenden, normalisierten CIDRs an — der Nutzer
 * sieht vor dem Speichern, was gesendet wird.
 */
export function IpRangeInput({ value, onChange, disabled }: IpRangeInputProps) {
  const { t } = useTranslation('mno-ip-ranges');
  const rows = value.length > 0 ? value : [''];

  const updateRow = (index: number, raw: string) => {
    const next = [...rows];
    next[index] = raw;
    onChange(next);
  };

  const addRow = () => onChange([...rows, '']);

  const removeRow = (index: number) => {
    const next = rows.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : ['']);
  };

  return (
    <div className="space-y-3">
      {rows.map((raw, index) => {
        const trimmed = raw.trim();
        const result = trimmed === '' ? null : parseIpRangeInput(raw);
        const hasError = result !== null && isParseError(result);

        return (
          <div key={index} className="space-y-1.5">
            <div className="flex items-start gap-2">
              <Input
                value={raw}
                onChange={(e) => updateRow(index, e.target.value)}
                placeholder={t('form.rangePlaceholder')}
                disabled={disabled}
                aria-label={t('form.rangeAriaLabel', { index: index + 1 })}
                aria-invalid={hasError || undefined}
                className={cn(hasError && 'border-destructive focus-visible:ring-destructive')}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeRow(index)}
                disabled={disabled}
                aria-label={t('form.removeRange')}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {result !== null && (
              hasError ? (
                <p className="text-sm text-destructive" role="alert">
                  {t(`errors.${(result as { error: string }).error}`)}
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="cidr-preview">
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  <span className="text-xs text-muted-foreground">{t('form.normalizedTo')}</span>
                  {!isParseError(result) &&
                    result.cidrs.map((cidr) => (
                      <Badge key={cidr} variant="secondary" className="font-mono">
                        {cidr}
                      </Badge>
                    ))}
                  {!isParseError(result) && (
                    <Badge variant="outline">IPv{result.version}</Badge>
                  )}
                </div>
              )
            )}
          </div>
        );
      })}

      <Button type="button" variant="outline" size="sm" onClick={addRow} disabled={disabled}>
        <Plus className="mr-2 h-4 w-4" />
        {t('form.addRange')}
      </Button>
    </div>
  );
}
